'use client';

/**
 * CollectionDynamicFields — custom Field component for `CollectionItems.dynamicFields`.
 *
 * Watches the selected `type` (a CollectionConfig), fetches that config's
 * `dynamicFields` definitions, and renders an input per definition. Values are
 * stored as a single JSON object keyed by each definition's `name`.
 * Required values are also enforced server-side by `validateDynamicFields`.
 */
import {
  addArrayRow,
  arrayRowKey,
  arrayRows,
  asDynamicFieldType,
  fetchFieldDefinitions,
  groupValue,
  isDynamicValueEmpty,
  relationshipId,
  removeArrayRow,
  setArrayRowValue,
} from '@/utils/dynamic-fields';
import {
  Button,
  CheckboxInput,
  Collapsible,
  FieldError,
  FieldLabel,
  Pill,
  SelectInput,
  TextareaInput,
  TextInput,
  useDocumentInfo,
  useField,
  useFormFields,
  useFormSubmitted,
} from '@payloadcms/ui';
import { useEffect, useState } from 'react';

import './styles.scss';

import type { DynamicFieldDefinition, DynamicFieldType } from '@/utils/dynamic-fields';

type DynamicValues = Record<string, unknown>;

interface ControlProps {
  definition: DynamicFieldDefinition;
  path: string;
  value: unknown;
  showError: boolean;
  /** Payload's anchored error tooltip; renders nothing while the value is valid. */
  error: React.ReactNode;
  readOnly: boolean;
  onChange: (next: unknown) => void;
}

const hint = (text: string) => <p className="field-description">{text}</p>;

/**
 * Native inputs for the types @payloadcms/ui exposes no standalone control for.
 * `DatePicker` would round-trip through a `Date`, which can shift the day across
 * timezones — a native date input deals in plain `YYYY-MM-DD`.
 */
const NativeControl = ({
  definition,
  path,
  fieldClass,
  showError,
  error,
  children,
}: Pick<ControlProps, 'definition' | 'path' | 'showError' | 'error'> & { fieldClass: string; children: React.ReactNode }) => (
  <div className={`field-type ${fieldClass}${showError ? ' error' : ''}`}>
    <FieldLabel label={definition.label} required={definition.required} htmlFor={path} />
    {error}
    {children}
  </div>
);

/**
 * The heading of a group or an array field. An `h4` rather than a field label: the name of a group is a
 * heading over its children, not a label for one input. Payload's own Group field does the same with
 * an `h3`, one level up from here because "Dynamic fields" already owns that level.
 */
const NestedHeader = ({ definition }: Pick<ControlProps, 'definition'>) => (
  <div className="fv-dynamic-fields__nested-header">
    <h4 className="fv-dynamic-fields__nested-title">{definition.label}</h4>
    {definition.required && <span className="required">*</span>}
  </div>
);

const CONTROLS: Record<DynamicFieldType, (props: ControlProps) => React.ReactNode> = {
  'text-field': ({ definition, path, value, showError, error, readOnly, onChange }) => (
    <TextInput
      path={path}
      label={definition.label}
      required={definition.required}
      showError={showError}
      Error={error}
      hasMany={false}
      readOnly={readOnly}
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  ),
  'textarea-field': ({ definition, path, value, showError, error, readOnly, onChange }) => (
    <TextareaInput
      path={path}
      label={definition.label}
      required={definition.required}
      showError={showError}
      Error={error}
      readOnly={readOnly}
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  ),
  // Payload's Checkbox *field* supplies the `field-type checkbox` wrapper that
  // carries the bottom margin; CheckboxInput on its own has none.
  'checkbox-field': ({ definition, path, value, showError, error, readOnly, onChange }) => (
    <div className={`field-type checkbox fv-dynamic-fields__checkbox${showError ? ' error' : ''}`}>
      {error}
      <CheckboxInput
        id={path}
        name={definition.name}
        label={definition.label}
        required={definition.required}
        readOnly={readOnly}
        checked={Boolean(value)}
        onToggle={(e) => onChange(e.target.checked)}
      />
    </div>
  ),
  'select-field': ({ definition, path, value, showError, error, readOnly, onChange }) => (
    <SelectInput
      path={path}
      name={definition.name}
      label={definition.label}
      required={definition.required}
      showError={showError}
      Error={error}
      options={definition.options ?? []}
      readOnly={readOnly}
      value={(value as string) ?? ''}
      onChange={(option) => onChange(Array.isArray(option) ? option[0]?.value : option?.value)}
    />
  ),
  'number-field': ({ definition, path, value, showError, error, readOnly, onChange }) => (
    <NativeControl definition={definition} path={path} showError={showError} error={error} fieldClass="number">
      <input
        id={path}
        type="number"
        disabled={readOnly}
        value={(value as number | string) ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.valueAsNumber)}
      />
    </NativeControl>
  ),
  'date-field': ({ definition, path, value, showError, error, readOnly, onChange }) => (
    <NativeControl definition={definition} path={path} showError={showError} error={error} fieldClass="text">
      <input id={path} type="date" disabled={readOnly} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
    </NativeControl>
  ),
  'group-field': ({ definition, path, value, showError, error, readOnly, onChange }) => {
    const object = groupValue(value);

    return (
      <div className={`field-type fv-dynamic-fields__nested${showError ? ' error' : ''}`}>
        <NestedHeader definition={definition} />
        {error}
        <div className="fv-dynamic-fields__nested-body">
          <ChildFields
            definitions={definition.fields ?? []}
            values={object}
            path={path}
            readOnly={readOnly}
            onChange={(name, next) => onChange({ ...object, [name]: next })}
          />
        </div>
      </div>
    );
  },
  'array-field': ({ definition, path, value, showError, error, readOnly, onChange }) => {
    const rows = arrayRows(value);

    return (
      <div className={`field-type fv-dynamic-fields__nested${showError ? ' error' : ''}`}>
        <NestedHeader definition={definition} />
        {error}
        <div className="fv-dynamic-fields__nested-body">
          {rows.map((row, index) => (
            <Collapsible
              className="fv-dynamic-fields__row"
              key={arrayRowKey(row, index)}
              header={
                <div className="fv-dynamic-fields__row-header">
                  <Pill pillStyle="light-gray">{String(index + 1).padStart(2, '0')}</Pill>
                  <span>{definition.label}</span>
                </div>
              }
              actions={
                readOnly ? undefined : (
                  <Button
                    buttonStyle="icon-label"
                    className="fv-dynamic-fields__remove"
                    icon="x"
                    iconPosition="left"
                    onClick={() => onChange(removeArrayRow(value, index))}
                    round
                    tooltip="Remove"
                  />
                )
              }
            >
              <ChildFields
                definitions={definition.fields ?? []}
                values={row}
                path={`${path}.${index}`}
                readOnly={readOnly}
                onChange={(name, next) => onChange(setArrayRowValue(value, index, name, next))}
              />
            </Collapsible>
          ))}
          {!readOnly && (
            <Button
              buttonStyle="icon-label"
              className="fv-dynamic-fields__add"
              icon="plus"
              iconPosition="left"
              onClick={() => onChange(addArrayRow(value))}
            >
              {`Add ${definition.label}`}
            </Button>
          )}
        </div>
      </div>
    );
  },
};

/** One level of definitions. The top level and the inside of a group both use this, so they match. */
const ChildFields = ({
  definitions,
  values,
  path,
  readOnly,
  onChange,
}: {
  definitions: DynamicFieldDefinition[];
  values: DynamicValues;
  path: string;
  readOnly: boolean;
  onChange: (name: string, next: unknown) => void;
}) => {
  const submitted = useFormSubmitted();

  // `render-fields` is what spaces stacked fields in the admin: the rule is
  // `.render-fields > .field-type`, so each control has to be a direct child of it.
  return (
    <div className="render-fields">
      {definitions.map((definition) => {
        const showError = submitted && Boolean(definition.required) && isDynamicValueEmpty(definition, values[definition.name]);
        const fieldPath = `${path}.${definition.name}`;
        const Control = CONTROLS[asDynamicFieldType(definition.blockType)];

        return (
          <Control
            key={definition.name}
            definition={definition}
            path={fieldPath}
            value={values[definition.name]}
            showError={showError}
            error={<FieldError message={`${definition.label} is required`} path={fieldPath} showError={showError} />}
            readOnly={readOnly}
            onChange={(next) => onChange(definition.name, next)}
          />
        );
      })}
    </div>
  );
};

export const CollectionDynamicFields: React.FC<{ path: string; readOnly?: boolean }> = ({ path, readOnly = false }) => {
  const { value, setValue } = useField<DynamicValues>({ path });
  const typeValue = useFormFields(([fields]) => fields?.type?.value as unknown);
  const typeId = relationshipId(typeValue);
  const { hasSavePermission } = useDocumentInfo();
  const locked = readOnly || hasSavePermission === false;

  const [definitions, setDefinitions] = useState<DynamicFieldDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadDefinitions = async () => {
      if (!typeId) {
        setDefinitions([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const loaded = await fetchFieldDefinitions(typeId);
        if (!cancelled) setDefinitions(loaded);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadDefinitions();
    return () => {
      cancelled = true;
    };
  }, [typeId]);

  const values = value || {};
  const update = (name: string, next: unknown) => setValue({ ...values, [name]: next });

  if (!typeId) return hint('Select a Collection Config type above to load its fields.');
  if (loading) return hint('Loading fields…');
  if (error) return <p className="field-error">Error loading fields: {error}</p>;
  if (!definitions.length) return hint('This Collection Config defines no fields.');

  return (
    <div className="field-type fv-dynamic-fields">
      <h3>Dynamic fields</h3>
      <p className="field-description fv-dynamic-fields__intro">Fields defined by the selected Collection Config.</p>
      <ChildFields definitions={definitions} values={values} path={path} readOnly={locked} onChange={update} />
    </div>
  );
};
